import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * VOLL: o Alert do shadcn só tem `default`/`destructive`. Adicionamos o eixo
 * `color` (quarteto semântico da paleta v2) × as variantes `outline`/`soft`.
 * Cada cor só declara três custom properties
 * locais; as variantes consomem — é o que mantém a troca de valor por coluna
 * funcionando na galeria de decisões.
 */
const alertVariants = cva(
  // VOLL: o `row-span-2` do ícone é condicionado à PRESENÇA da descrição. Ele existe
  // para o ícone cobrir as duas linhas (título + descrição); num alert de uma linha só,
  // ele cria uma segunda linha implícita de altura zero e o `gap-0.5` cobra 2px de
  // row-gap dessa linha vazia — 2px fantasma em todo Alert com ícone e sem descrição.
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 has-data-[slot=alert-description]:*:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current',
        outline:
          'border-(--alert-line) bg-card text-(--alert-strong) *:data-[slot=alert-description]:text-inherit *:data-[slot=alert-description]:opacity-85',
        soft: 'border-transparent bg-(--alert-soft) text-(--alert-strong) *:data-[slot=alert-description]:text-inherit *:data-[slot=alert-description]:opacity-85',
      },
      color: {
        neutral:
          '[--alert-line:var(--border)] [--alert-soft:var(--muted)] [--alert-strong:var(--card-foreground)]',
        info: '[--alert-line:var(--info)] [--alert-soft:var(--info-soft)] [--alert-strong:var(--info-strong)]',
        success:
          '[--alert-line:var(--success)] [--alert-soft:var(--success-soft)] [--alert-strong:var(--success-strong)]',
        warning:
          '[--alert-line:var(--warning-border)] [--alert-soft:var(--warning-soft)] [--alert-strong:var(--warning-strong)]',
        danger:
          '[--alert-line:var(--destructive)] [--alert-soft:var(--danger-soft)] [--alert-strong:var(--danger-strong)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      color: 'neutral',
    },
  },
);

function Alert({
  className,
  variant,
  color,
  ...props
}: Omit<React.ComponentProps<'div'>, 'color'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-color={color}
      role="alert"
      className={cn(alertVariants({ variant, color }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="alert-action" className={cn('absolute top-2 right-2', className)} {...props} />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
